import { useState } from "react";
import { toast } from "sonner";
import { Upload, Check } from "lucide-react";
import { api } from "../lib/api";

const MEDIUMS = ["Charcoal", "Coloured Pencil", "Acrylic (photorealistic)", "Mixed / Not sure"];
const SIZES = ["A4 (~21×30cm)", "A3 (~30×42cm)", "A2 (~42×59cm)", "Custom / Large scale"];
const BUDGETS = ["$200 – $500", "$500 – $1,000", "$1,000 – $2,500", "$2,500+", "Flexible"];

export default function Commissions() {
    const [form, setForm] = useState({ name: "", email: "", medium: MEDIUMS[0], size: SIZES[0], description: "", budget: BUDGETS[0], reference_path: null });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.description.trim()) {
            toast.error("Please fill in your name, email, and description");
            return;
        }
        setBusy(true);
        try {
            let reference_path = null;
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const upR = await api.post("/commissions/reference", fd);
                reference_path = upR.data.path;
            }
            await api.post("/commissions", { ...form, reference_path });
            setDone(true);
            toast.success("Inquiry received — I'll respond within 3 business days.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Something went wrong");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="relative z-10 pt-40 pb-32 min-h-screen text-center px-6" data-testid="commission-success">
                <div className="max-w-2xl mx-auto">
                    <Check className="w-14 h-14 text-[#8C3A3A] mx-auto mb-8" />
                    <div className="label-caps text-[#8C3A3A] mb-4">Inquiry Received</div>
                    <h1 className="font-serif-display text-5xl text-[#1A1A1A] font-light tracking-tighter mb-6 italic">Thank you.</h1>
                    <p className="text-[#5A5A5A] leading-relaxed">
                        Your commission request has been sent to the studio. I'll respond personally within 3 business
                        days with a quote and timeline.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative z-10 pt-24 sm:pt-32 pb-24" data-testid="commissions-page">
            <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
                <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16 lg:gap-24 items-start">
                    <div className="lg:sticky lg:top-32">
                        <div className="label-caps text-[#8C3A3A] mb-4">Custom Works</div>
                        <h1 className="font-serif-display text-5xl sm:text-6xl font-light tracking-tighter text-[#1A1A1A] leading-none mb-8">
                            Commission<br /><span className="italic text-[#5A5A5A]">a portrait.</span>
                        </h1>
                        <p className="text-[#5A5A5A] leading-relaxed mb-8">
                            Every commission is a personal collaboration. Share your reference, your intent, and your
                            preferred medium. I'll respond within three days with a quote and timeline.
                        </p>
                        <div className="space-y-4 text-sm">
                            <div className="flex justify-between py-3 border-b border-black/[0.06]"><span className="label-caps">Turnaround</span><span className="text-[#1A1A1A]">4 – 10 weeks</span></div>
                            <div className="flex justify-between py-3 border-b border-black/[0.06]"><span className="label-caps">Payment</span><span className="text-[#1A1A1A]">50% deposit</span></div>
                            <div className="flex justify-between py-3 border-b border-black/[0.06]"><span className="label-caps">Revisions</span><span className="text-[#1A1A1A]">2 rounds included</span></div>
                        </div>
                    </div>

                    <form onSubmit={submit} className="space-y-8" data-testid="commission-form">
                        <div>
                            <label className="label-caps block mb-2">Your name</label>
                            <input required value={form.name} onChange={upd("name")} className="underline-input" placeholder="Full name" data-testid="commission-name" />
                        </div>
                        <div>
                            <label className="label-caps block mb-2">Email</label>
                            <input required type="email" value={form.email} onChange={upd("email")} className="underline-input" placeholder="you@example.com" data-testid="commission-email" />
                        </div>

                        <div className="grid sm:grid-cols-2 gap-8">
                            <div>
                                <label className="label-caps block mb-2">Preferred medium</label>
                                <select value={form.medium} onChange={upd("medium")} className="underline-input appearance-none" data-testid="commission-medium">
                                    {MEDIUMS.map((m) => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Size</label>
                                <select value={form.size} onChange={upd("size")} className="underline-input appearance-none" data-testid="commission-size">
                                    {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="label-caps block mb-2">Budget range</label>
                            <select value={form.budget} onChange={upd("budget")} className="underline-input appearance-none" data-testid="commission-budget">
                                {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className="label-caps block mb-2">Describe your vision</label>
                            <textarea required value={form.description} onChange={upd("description")} rows="5" className="underline-input resize-none" placeholder="Subject, mood, references, intent..." data-testid="commission-description" />
                        </div>

                        <div>
                            <label className="label-caps block mb-3">Reference image (optional)</label>
                            <label className="flex items-center gap-3 border border-dashed border-black/15 px-6 py-6 cursor-pointer hover:border-black/40 transition-colors" data-testid="commission-file-label">
                                <Upload className="w-4 h-4 text-[#5A5A5A]" />
                                <span className="text-sm text-[#5A5A5A]">
                                    {file ? file.name : "Attach JPG, PNG, or WEBP (max 10MB)"}
                                </span>
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="commission-file-input" />
                            </label>
                        </div>

                        <button
                            type="submit"
                            disabled={busy}
                            className="bg-[#1A1A1A] text-[#FAFAF7] px-10 py-4 rounded-full text-sm tracking-wide font-medium hover:bg-[#8C3A3A] hover:text-white disabled:opacity-50 transition-colors"
                            data-testid="commission-submit"
                        >
                            {busy ? "Sending..." : "Send Inquiry"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
